import { ReactComponent as accountCircle } from '../../../icons/account_circle.svg';
import ButtonField from '../../buttonField/buttonField';
import { ButtonFieldProps } from '../../buttonField/types/buttonFieldProps';
import Modal from '../../modalWindow/modal';
import RadioButtonField from '../../radioButtonField/radioButtonField';
import { RadioButtonFieldProps } from '../../radioButtonField/types/radioButtonFieldProps';
import { classNameBtn, iconPosition } from '../../shared/button/types/buttonProps';
import { stylizationVerticalAlign } from '../../shared/stylization';
import { UserOptionsProps } from './types/userOptionsProps';
import { Option } from "../../../components/shared/selectInput/types/selectInputProps";
import CheckboxMultiField from '../../checkboxMultiField/checkboxMultiField';
import { useContext, useEffect, useState } from 'react';
import AuthContext, { UserConfig } from '../../../contexts/authContext';
import { YesNoOptions, handleRememberLogInDetailsToggle, notificationFrequency, notificationTypeShown, updateOptions } from '../../../helpers/userConfig';
import './../userField.scss';
import { setUserConfigIdb } from '../../../database/indexedDbReadWrite';
import { apiContext } from '../../../contexts/apiContext';
import { getUserNotificationConfig, saveEmailNotificationUserConfig } from '../../../services/administrationService/administrationService';
import { notifyError } from '../../snackbar/snackbar';
import Spinner from '../../spinner/spinner';
import { ClassNameSpinner } from '../../spinner/types/spinnerProps';

const UserOptions = (props: UserOptionsProps) => {
    const api = useContext(apiContext);
    const { user, userConfig, setUserConfig, username, userId } = useContext(AuthContext);
    const [currentConfig, setCurrentConfig] = useState<UserConfig>(userConfig);
    const [currentNotifyByEmail, setCurrentNotifyByEmail] = useState<boolean>(false);
    const [newNotifyByEmail, setNewNotifyByEmail] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [disableActionBtn, setDisableActionBtn] = useState<boolean>(false);

    useEffect(() => {
        setDisableActionBtn(true);
        setLoading(true);
        getUserNotificationConfig(api.apiUrl + api.apiVersion, userId)
        .then((res: any) => {
            var config: any = Object.assign({}, userConfig);  
            setCurrentConfig(config);
            setCurrentNotifyByEmail(res.czY_EMAIL === 1);
            setNewNotifyByEmail(res.czY_EMAIL === 1);
            setDisableActionBtn(false);
            setLoading(false);
        })
        .catch(() => {
            notifyError({ message: "Próba pobrania aktualnych ustawień użytkownika zakończona niepowodzeniem" })
            setLoading(false);
        });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Radio buttons
    const onClickRadioButton = (value: any, inputId: string, isYesNo: boolean) => { 
        var val = isYesNo ? value === 1 : value;
        inputId === "notifyByEmail" ? setNewNotifyByEmail(val) : setCurrentConfig({...currentConfig, [inputId]: val});              
    }

    const getRadioButtonProps = (label: string, options: Option[], inputId: string, isYesNo: boolean, selectedValue: any, disabledValue?: any): RadioButtonFieldProps => {
        return {
            labelProps: {
                style: {
                    paddingTop: '10px',
                    marginBottom: '10px',
                    display: "inline-block",
                    width: '100%'
                },
                labelname: label
            },
            radioButtonProps: {
                inputName: inputId,
                onClick: (event: any, value: number) => { onClickRadioButton(value, inputId, isYesNo) }
            },
            style: {
                width:'100%', 
                paddingBottom: '10px',
                textAlign: 'left',
                whiteSpace: 'pre-line'
            },
            Option: options,
            selectedValue: selectedValue,
            disabledValue: disabledValue
        }
    }

    // Checkboxes
    const onChangeCheckbox = (value: string | number | undefined, checked: boolean) => {       
        switch (value) {
            case 0: 
                setCurrentConfig({...currentConfig, checkForUpdates: {...currentConfig.checkForUpdates, onOnlineToggle: !currentConfig.checkForUpdates.onOnlineToggle}});
                break;
            case 1: 
                setCurrentConfig({...currentConfig, checkForUpdates: {...currentConfig.checkForUpdates, onStartUp: !currentConfig.checkForUpdates.onStartUp}});
                break;   
        }
    }

    const getSelectedCheckboxes = () => {
        var selected: number[] = [];
        if (currentConfig?.checkForUpdates?.onOnlineToggle) selected.push(0);
        if (currentConfig?.checkForUpdates?.onStartUp) selected.push(1);
        return selected;
    }
    
    // Action buttons
    const getBtnProps = (title: string, className: string, onClick: React.MouseEventHandler<HTMLButtonElement>, disable: boolean): ButtonFieldProps => {
        return {
            BtnProps: {
                onClick: onClick,
                className: `${classNameBtn.Btn} ${className} body-txt-medium`,
                disabled: disable
            },
            placeHolder: title,
            placeHolderStyle: stylizationVerticalAlign,
            iconPosition: iconPosition.Start
        };
    } 

    const handleSaveUserOptionsClick = () => {
        handleRememberLogInDetails();
        handleEmailNotificationSave();
        setUserConfig(currentConfig);
        setUserConfigIdb({ username: username, name: user, config: currentConfig });
    }

    const handleRememberLogInDetails = () => {
        var isChanged = currentConfig.rememberLogInDetails !== userConfig.rememberLogInDetails;
        if (isChanged) handleRememberLogInDetailsToggle(currentConfig.rememberLogInDetails);
    }

    const handleEmailNotificationSave = () => {
        var isChanged = currentNotifyByEmail !== newNotifyByEmail;

        if (isChanged) {
            setLoading(true);
            saveEmailNotificationUserConfig(api.apiUrl + api.apiVersion, newNotifyByEmail, userId)
            .then(() => {
                setLoading(false);
                props.onClose();
            })
            .catch(() => {
                notifyError({ message: "Próba zapisania ustawień użytkownika zakończona niepowodzeniem" })
                setLoading(false);
            })
        } else { props.onClose(); }
        
    }

    return (
        <Modal
            title={"Opcje użytkownika"}
            titleIcon={accountCircle}
            iconClass='titleIconBlack'
            width={'100%'}
            height={'100vh'}
            onClose={props.onClose}> 
            <>
                {loading && <div><Spinner spinnerClass={ClassNameSpinner.Big}/></div>}
                {!loading && 
                <>
                    <div style={{padding: "0px 20px 20px 20px"}}>
                        <div>
                            <h3>Logowanie</h3>
                            <RadioButtonField {...getRadioButtonProps(
                                "Zapamiętaj dane do logowania", 
                                YesNoOptions, 
                                "rememberLogInDetails", 
                                true,
                                currentConfig.rememberLogInDetails ? 1 : 0,
                                1)} />
                        </div>
                        <div style={{paddingTop: "20px"}}>
                            <h3>Ogólne</h3>
                            <RadioButtonField {...getRadioButtonProps(
                                "Przełącz automatycznie w tryb online po uruchomieniu aplikacji", 
                                YesNoOptions, 
                                "automaticOnlineMode", 
                                true,
                                currentConfig.automaticOnlineMode ? 1 : 0)} />
                            <div className='userOptionsCheckboxFieldWrapper'>
                                <CheckboxMultiField 
                                    onChange={onChangeCheckbox} 
                                    labelName="Sprawdzaj aktualizacje" 
                                    checkboxOptions={updateOptions} 
                                    selectedValues={getSelectedCheckboxes()} 
                                    labelClass=' '
                                    className='userOptionsCheckboxes'/>
                            </div>
                        </div>
                        <div style={{paddingTop: "20px"}}>
                            <h3>Wiadomości i powiadomienia</h3>
                            <RadioButtonField {...getRadioButtonProps(
                                "Częstotliwość weryfikacji przychodzących powiadomień i wiadomości", 
                                notificationFrequency, 
                                "notificationFrequency", 
                                false,
                                currentConfig.notificationFrequency)} />
                            <RadioButtonField {...getRadioButtonProps(
                                "Wyświetlaj wiadomości i powiadomienia", 
                                notificationTypeShown, 
                                "notificationTypeShown", 
                                false,
                                currentConfig.notificationTypeShown)} />
                            <RadioButtonField {...getRadioButtonProps(
                                "Wysyłaj kopię wiadomości i powiadomień na e-mail", 
                                YesNoOptions, 
                                "notifyByEmail", 
                                true,
                                newNotifyByEmail ? 1 : 0)} />
                        </div>
                    </div>
                    <div className="modalBtns" style={{paddingRight: "10px"}}>
                        <ButtonField {...getBtnProps("Anuluj", classNameBtn.LargeSecondary, () => { props.onClose() }, false)}></ButtonField>
                        <ButtonField {...getBtnProps("Zapisz zmiany", classNameBtn.Large, () => { handleSaveUserOptionsClick() }, disableActionBtn)}></ButtonField>
                    </div>
                </>}
            </>
        </Modal>
    )
}

export default UserOptions;